import { useCallback, useEffect, useState } from "react";
import type { RoomJoinMode } from "../../shared/types";
import {
  createRoom as apiCreateRoom,
  deleteRoom as apiDeleteRoom,
  fetchRooms,
  inviteToRoom as apiInvite,
  updateRoom as apiUpdateRoom,
  updateRoomName as apiUpdateRoomName,
  type RoomWithAccess,
  type RoomJoinDecision,
  type Session,
} from "../lib/api";

export function useRooms(session: Session | null) {
  const [rooms, setRooms] = useState<RoomWithAccess[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const loadRooms = useCallback(
    async (q?: string) => {
      if (!session) {
        setRooms([]);
        return;
      }
      setLoading(true);
      try {
        const list = await fetchRooms(session, q);
        setRooms(list);
      } finally {
        setLoading(false);
      }
    },
    [session]
  );

  useEffect(() => {
    void loadRooms(searchQuery || undefined);
  }, [loadRooms, searchQuery]);

  const createRoom = useCallback(
    async (
      name: string,
      isPrivate: boolean,
      layoutId?: string,
      wallThemeId?: string,
      floorThemeId?: string,
      options?: {
        joinMode?: RoomJoinMode;
        joinPassword?: string;
        tokenGateContract?: string;
        allowKnock?: boolean;
      }
    ) => {
      if (!session) throw new Error("Not connected");
      const room = await apiCreateRoom(
        session,
        name,
        isPrivate,
        layoutId,
        undefined,
        wallThemeId,
        floorThemeId,
        options
      );
      await loadRooms(searchQuery || undefined);
      return room;
    },
    [session, loadRooms, searchQuery]
  );

  const renameRoom = useCallback(
    async (roomId: string, name: string) => {
      if (!session) throw new Error("Not connected");
      await apiUpdateRoomName(session, roomId, name);
      await loadRooms(searchQuery || undefined);
    },
    [session, loadRooms, searchQuery]
  );

  const toggleRoomPrivacy = useCallback(
    async (roomId: string, isPrivate: boolean) => {
      if (!session) throw new Error("Not connected");
      await apiUpdateRoom(session, roomId, { isPrivate });
      await loadRooms(searchQuery || undefined);
    },
    [session, loadRooms, searchQuery]
  );

  const inviteToRoom = useCallback(
    async (roomId: string, inviteeAddress: string) => {
      if (!session) throw new Error("Not connected");
      await apiInvite(session, roomId, inviteeAddress);
    },
    [session]
  );

  const deleteRoom = useCallback(
    async (roomId: string) => {
      if (!session) throw new Error("Not connected");
      await apiDeleteRoom(session, roomId);
      await loadRooms(searchQuery || undefined);
    },
    [session, loadRooms, searchQuery]
  );

  return {
    rooms,
    loading,
    loadRooms,
    createRoom,
    renameRoom,
    toggleRoomPrivacy,
    inviteToRoom,
    deleteRoom,
    searchQuery,
    setSearchQuery,
  };
}
